import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useTranslation } from 'react-i18next';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, Edit, Trash2, UserCheck, UserX, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Employees = () => {
  const { user, token } = useAuth();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newlyCreatedEmployee, setNewlyCreatedEmployee] = useState(null); // Store newly created employee info
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'employee',
    max_files: 50,
    employee_roles: [],
    phone: '',
    address: '',
    diploma: '',
    hire_date: '',
    job_title: '',
    department: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchAvailableRoles();
  }, []);

  useEffect(() => {
    // Show download prompt after employee creation
    if (newlyCreatedEmployee) {
      const shouldDownload = window.confirm(
        `Employé créé avec succès!\n\nVoulez-vous télécharger le PDF avec les identifiants maintenant?\n\n⚠️ C'est la seule fois où vous pourrez télécharger le PDF avec le mot de passe d'origine. Sinon, un nouveau mot de passe temporaire sera généré.`
      );
      
      if (shouldDownload) {
        downloadEmployeeInfo(newlyCreatedEmployee.id, newlyCreatedEmployee.name, newlyCreatedEmployee.password);
      } else {
        setNewlyCreatedEmployee(null);
      }
    }
  }, [newlyCreatedEmployee]);

  const fetchAvailableRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setAvailableRoles(response.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        const employeesList = response.data.filter(u => u.role === 'employee');
        setEmployees(employeesList);
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error(t('error'));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await axios.put(
          `${API}/users/${editingEmployee.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Employé modifié avec succès');
      } else {
        // Use new hierarchical create endpoint with all employee information
        const response = await axios.post(
          `${API}/users/create-employee`,
          null,
          { 
            params: {
              email: formData.email,
              password: formData.password,
              full_name: formData.full_name,
              employee_roles: formData.employee_roles.join(','),
              phone: formData.phone || undefined,
              address: formData.address || undefined,
              diploma: formData.diploma || undefined,
              hire_date: formData.hire_date || undefined,
              job_title: formData.job_title || undefined,
              department: formData.department || undefined,
              emergency_contact_name: formData.emergency_contact_name || undefined,
              emergency_contact_phone: formData.emergency_contact_phone || undefined
            },
            headers: { Authorization: `Bearer ${token}` } 
          }
        );
        
        // Store newly created employee info with password for PDF generation
        setNewlyCreatedEmployee({
          id: response.data.user_id,
          name: formData.full_name,
          password: formData.password
        });
        
        if (response.data.requires_approval) {
          toast.success('Employé créé - En attente d\'approbation par le super administrateur');
        } else {
          toast.success('Employé créé avec succès! Voulez-vous télécharger le PDF avec les identifiants?');
        }
      }
      setDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      // Handle error properly - extract message from validation error
      let errorMessage = t('error');
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (error.response.data.detail.msg) {
          errorMessage = error.response.data.detail.msg;
        }
      }
      toast.error(errorMessage);
      console.error('Error creating employee:', error);
    }
  };

  const toggleActive = async (employeeId) => {
    try {
      await axios.put(
        `${API}/users/${employeeId}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Statut modifié');
      fetchEmployees();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) return;
    
    try {
      await axios.delete(`${API}/users/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employé supprimé');
      fetchEmployees();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const downloadEmployeeInfo = async (employeeId, employeeName, providedPassword = null) => {
    try {
      toast.info('Génération du dossier employé en cours...');
      
      const response = await axios.post(
        `${API}/users/${employeeId}/download-employee-info`,
        null,
        {
          params: providedPassword ? { password: providedPassword } : {},
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dossier_Employe_${employeeName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      if (providedPassword) {
        toast.success('📄 Dossier employé généré avec le mot de passe défini!');
      } else {
        toast.success('📄 Dossier employé généré ! Un nouveau mot de passe temporaire a été créé.');
        toast.warning('⚠️ L\'employé devra changer son mot de passe à la première connexion.', { duration: 5000 });
      }
      
      // Clear newly created employee state after download
      setNewlyCreatedEmployee(null);
    } catch (error) {
      toast.error('Erreur lors de la génération du dossier employé');
      console.error('Error downloading employee info:', error);
    }
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      full_name: employee.full_name,
      email: employee.email,
      password: '',
      role: employee.role,
      max_files: employee.max_files || 50,
      employee_roles: employee.employee_roles || [],
      phone: employee.phone || '',
      address: employee.address || '',
      diploma: employee.metadata?.diploma || '',
      hire_date: employee.metadata?.hire_date || '',
      job_title: employee.metadata?.job_title || '',
      department: employee.metadata?.department || '',
      emergency_contact_name: employee.metadata?.emergency_contact_name || '',
      emergency_contact_phone: employee.metadata?.emergency_contact_phone || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'employee',
      max_files: 50,
      employee_roles: [],
      phone: '',
      address: '',
      diploma: '',
      hire_date: '',
      job_title: '',
      department: '',
      emergency_contact_name: '',
      emergency_contact_phone: ''
    });
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({ ...formData, password });
    toast.success('Mot de passe généré : ' + password, { duration: 10000 });
    // Copy to clipboard
    navigator.clipboard.writeText(password);
    toast.info('Mot de passe copié dans le presse-papiers');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="employees-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {t('employees')}
            </h1>
            <p className="text-lg text-gray-600">
              {employees.length} {employees.length === 1 ? 'employé' : 'employés'}
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700 shadow-lg"
                data-testid="add-employee-btn"
              >
                <Plus className="mr-2 h-5 w-5" />
                {t('add_employee')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingEmployee ? 'Modifier l\'employé' : t('add_employee')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Section: Informations de base */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations de base</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('full_name')} *</Label>
                      <Input
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required
                        placeholder="Nom complet"
                        data-testid="employee-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('email')} *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        disabled={editingEmployee}
                        placeholder="email@exemple.com"
                        data-testid="employee-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+1 (514) 123-4567"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>{t('password')} {editingEmployee && '(laisser vide pour ne pas changer)'} {!editingEmployee && '*'}</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={generatePassword}
                          className="text-xs"
                        >
                          Générer
                        </Button>
                      </div>
                      <Input
                        type="text"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required={!editingEmployee}
                        placeholder="Mot de passe"
                        data-testid="employee-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label>Adresse complète</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Rue Exemple, Montréal, QC, H1A 1A1"
                    />
                  </div>
                </div>

                {/* Section: Informations professionnelles */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations professionnelles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Poste / Titre</Label>
                      <Input
                        value={formData.job_title}
                        onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                        placeholder="Conseiller en immigration"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Département</Label>
                      <Input
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        placeholder="Service client"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>Date d'embauche</Label>
                      <Input
                        type="date"
                        value={formData.hire_date}
                        onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre maximal de dossiers</Label>
                      <Input
                        type="number"
                        value={formData.max_files}
                        onChange={(e) => setFormData({ ...formData, max_files: parseInt(e.target.value) })}
                        placeholder="50"
                        data-testid="employee-maxfiles-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label>Diplôme / Formation</Label>
                    <Input
                      value={formData.diploma}
                      onChange={(e) => setFormData({ ...formData, diploma: e.target.value })}
                      placeholder="Baccalauréat en droit, Certificat RCIC"
                    />
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label>Rôle d'Employé *</Label>
                    <Select
                      value={formData.employee_roles[0] || ''}
                      onValueChange={(value) => setFormData({ ...formData, employee_roles: [value] })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un rôle" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRoles.map(role => (
                          <SelectItem key={role.value} value={role.value}>
                            <div>
                              <div className="font-medium">{role.label}</div>
                              <div className="text-xs text-gray-500">{role.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.employee_roles[0] && (
                      <p className="text-xs text-gray-600 mt-1">
                        {availableRoles.find(r => r.value === formData.employee_roles[0])?.permissions_summary}
                      </p>
                    )}
                  </div>
                </div>

                {/* Section: Contact d'urgence */}
                <div className="pb-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact d'urgence</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom du contact</Label>
                      <Input
                        value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                        placeholder="Nom du contact d'urgence"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone du contact</Label>
                      <Input
                        type="tel"
                        value={formData.emergency_contact_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                        placeholder="+1 (514) 123-4567"
                      />
                    </div>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700"
                  data-testid="submit-employee-btn"
                >
                  {editingEmployee ? 'Modifier' : 'Créer l\'employé'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Nom</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Rôles</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Dossiers max</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Statut</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Créé le</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50" data-testid={`employee-row-${employee.id}`}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {employee.full_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {employee.email}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {employee.employee_roles && employee.employee_roles.length > 0 ? (
                            employee.employee_roles.map(role => {
                              const roleLabels = {
                                'sous_admin': '🔑 Sous-admin',
                                'comptabilite': '💰 Compta',
                                'controleur': '✅ Contrôle',
                                'conseiller': '💼 Conseil',
                                'assistant': '📋 Assistant'
                              };
                              return (
                                <Badge key={role} variant="outline" className="text-xs">
                                  {roleLabels[role] || role}
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-xs text-gray-400">Aucun rôle</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {employee.max_files || 'Illimité'}
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          className={employee.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                        >
                          {employee.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {format(new Date(employee.created_at), 'dd/MM/yyyy')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {/* Protection: empêcher soundous de modifier lahroum */}
                          {(() => {
                            const isLahroum = employee.email === 'lahroum@lcsimmigration.com';
                            const currentUserIsLahroum = user?.email === 'lahroum@lcsimmigration.com';
                            const isProtected = isLahroum && !currentUserIsLahroum;
                            
                            return (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadEmployeeInfo(employee.id, employee.full_name)}
                                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 flex items-center gap-1"
                                  title="Télécharger le dossier complet de l'employé"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <span className="hidden xl:inline">Dossier</span>
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => !isProtected && openEditDialog(employee)}
                                  disabled={isProtected}
                                  data-testid={`edit-employee-${employee.id}`}
                                  title={isProtected ? "Vous n'avez pas les droits pour modifier cet utilisateur" : "Modifier"}
                                  className={isProtected ? "opacity-50 cursor-not-allowed" : ""}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => !isProtected && toggleActive(employee.id)}
                                  disabled={isProtected}
                                  data-testid={`toggle-employee-${employee.id}`}
                                  title={isProtected ? "Vous n'avez pas les droits pour désactiver cet utilisateur" : "Activer/Désactiver"}
                                  className={isProtected ? "opacity-50 cursor-not-allowed" : ""}
                                >
                                  {employee.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => !isProtected && handleDelete(employee.id)}
                                  disabled={isProtected}
                                  className={isProtected ? "opacity-50 cursor-not-allowed text-gray-400" : "text-red-600 hover:text-red-700"}
                                  title={isProtected ? "Vous n'avez pas les droits pour supprimer cet utilisateur" : "Supprimer"}
                                  data-testid={`delete-employee-${employee.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {employees.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                  <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Aucun employé</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Employees;